import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { useAppSelector } from '@/store/hooks';
import { getCurrentReport } from '@/store/selectors/transmissionReportSelectors';
import { useSendSupportMessageMutation } from '@/store/slices/api/supportApiSlice';
import { useGetGlobalFilterQuery } from '@/store/slices/api/userApiSlice';
import { useAuth } from '@/hooks/useAuth';

import { UserRole } from '@/types/user';

import styles from './SupportWidget.module.scss';

interface SupportContext {
  patientId?: string;
  page: string;
  timestamp: string;
  userId?: string;
  userEmail?: string;
}

const SupportWidget = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [message, setMessage] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const location = useLocation();
  const params = useParams();
  const { user } = useAuth();
  const [sendSupportMessage, { isLoading }] = useSendSupportMessageMutation();
  const { data: globalFilterData } = useGetGlobalFilterQuery();

  // Get transmission report data from Redux store if we're on a transmission report page
  const { id } = params as { id?: string };
  const reportId =
    id && location.pathname.includes('/transmissions/report/') ? parseInt(id) : undefined;
  const transmissionReportData = useAppSelector((state) =>
    reportId ? getCurrentReport(state, reportId) : null
  );

  // Check if user has access to support widget (organization has helpwiseFormId)
  const hasSupportAccess = useMemo(() => {
    // Admin users always have access
    if (user?.role === UserRole.InternalAdmin || user?.role === UserRole.ExternalAdmin) {
      return true;
    }

    const userClinics = globalFilterData?.clinics ?? [];
    // Check if any of the user's clinics (or their parent accounts) have helpwiseFormId
    return userClinics.some((clinic) => clinic.helpwiseFormId || clinic.parent?.helpwiseFormId);
  }, [globalFilterData, user?.role]);

  // Extract patient ID from URL params or transmission report data
  const patientId = useMemo(() => {
    // Check for patient UUID in URL params
    const { uuid } = params as { uuid?: string };

    // Patient profile pages use 'uuid'
    if (uuid && location.pathname.includes('/patients/')) {
      return uuid;
    }

    // For transmission report pages, get patient ID from Redux store
    // The URL pattern is /physician/transmissions/report/{reportId}
    if (reportId && transmissionReportData?.transmissionReport?.patient?.id) {
      return transmissionReportData.transmissionReport.patient.id;
    }

    // Debug logging to help troubleshoot
    if (reportId && !transmissionReportData) {
      console.log('SupportWidget: Report ID found but no transmission report data yet:', reportId);
    }

    return undefined;
  }, [params, location.pathname, reportId, transmissionReportData]);

  // Build context information
  const context: SupportContext = useMemo(
    () => ({
      patientId,
      page: location.pathname,
      timestamp: new Date().toISOString(),
      userId: user?.id,
      userEmail: user?.email
    }),
    [patientId, location.pathname, user]
  );

  const handleToggle = useCallback(() => {
    setIsExpanded(!isExpanded);
    if (!isExpanded) {
      setIsSubmitted(false);
      setMessage('');
      setError(null);
    }
  }, [isExpanded]);

  // Reset widget state when navigating to a new patient page
  const previousPatientId = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (patientId && patientId !== previousPatientId.current) {
      // We've navigated to a new patient page
      setIsExpanded(false);
      setIsSubmitted(false);
      setMessage('');
      setError(null);
    }
    previousPatientId.current = patientId;
  }, [patientId]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!message.trim() || !patientId) return;

      // Clear any existing errors
      setError(null);

      try {
        await sendSupportMessage({
          patientId,
          message: message.trim(),
          context
        }).unwrap();

        setIsSubmitted(true);
        setMessage('');

        // Auto-close after success
        setTimeout(() => {
          setIsExpanded(false);
        }, 2000);
      } catch (error) {
        console.error('Failed to send support message:', error);
        setError('Your message could not be sent. Please try again.');
      }
    },
    [message, patientId, context, sendSupportMessage]
  );

  const canSubmit = message.trim().length > 0 && patientId && !isLoading;
  const hasPatientContext = Boolean(patientId);

  // Don't render widget if user doesn't have support access
  if (!hasSupportAccess) {
    return null;
  }

  if (!isExpanded) {
    return (
      <div className={styles.widget}>
        <button
          className={styles.minimized}
          onClick={handleToggle}
          title="Need help? Click to open support chat">
          ?
        </button>
      </div>
    );
  }

  return (
    <div className={styles.widget}>
      <div className={styles.expanded}>
        <header className={styles.header}>
          <h3 className={styles.title}>Support</h3>
          <button className={styles.closeButton} onClick={handleToggle} title="Close">
            ×
          </button>
        </header>

        <div className={styles.content}>
          {!hasPatientContext && (
            <div className={styles.noPatientMessage}>
              No patient found for support. Please navigate to a patient page to send a support
              message.
            </div>
          )}

          {error && <div className={styles.errorMessage}>{error}</div>}

          {isSubmitted ? (
            <div className={styles.successMessage}>
              Your support message has been sent successfully! We'll get back to you soon.
            </div>
          ) : (
            <form className={styles.form} onSubmit={handleSubmit}>
              <textarea
                className={styles.textarea}
                placeholder={
                  hasPatientContext
                    ? 'Describe your issue or feedback...'
                    : 'Support messaging is disabled on this page'
                }
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                disabled={!hasPatientContext || isLoading}
                maxLength={1000}
              />

              <button type="submit" className={styles.submitButton} disabled={!canSubmit}>
                {isLoading ? (
                  <span className={styles.loading}>
                    <span className={styles.spinner}></span>
                    Sending...
                  </span>
                ) : (
                  'Send Message'
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportWidget;
